/**
 * OutcomeCard - Consumer Duty outcome card showing metrics and top causes
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../../../../ui/card';
import { Progress } from '../../../../../ui/progress';
import { ConductScoreCircle } from '../../../../../ui/conduct-score-circle';
import {
  Headphones,
  BookOpen,
  BadgePoundSterling,
  Package,
} from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { DeltaIndicator } from '../../../../common/components';
import type { OutcomeCardProps, ConsumerDutyOutcomeId } from '../../types';
import { CONSUMER_DUTY_OUTCOMES, DELTA_THRESHOLDS } from '../../constants';
import React from 'react';

const OUTCOME_ICONS: Record<ConsumerDutyOutcomeId, React.ComponentType<{ className?: string }>> = {
  consumer_support: Headphones,
  consumer_understanding: BookOpen,
  price_and_fair_value: BadgePoundSterling,
  product_design: Package,
};

export function OutcomeCard({
  outcome,
  onCauseClick,
  className,
}: OutcomeCardProps) {
  const metadata = CONSUMER_DUTY_OUTCOMES[outcome.id] ?? { color: '#6b7280' };
  const Icon = OUTCOME_ICONS[outcome.id] ?? Package;

  return (
    <Card className={cn('flex flex-col overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid={`outcome-card-${outcome.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/60"
              style={{ backgroundColor: `${metadata.color}20` }}
            >
              <span style={{ color: metadata.color }}>
                <Icon className="h-5 w-5" />
              </span>
            </div>
            <div>
              <CardTitle className="text-base">{outcome.name}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {outcome.description}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {/* Metrics Row */}
        <div className="flex flex-wrap items-center gap-4">
          <ConductScoreCircle
            score={outcome.avgConductScore}
            size="sm"
            showDecimal
          />
          <div className="flex items-baseline gap-1.5">
            <p className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] tabular-nums">
              {outcome.recordCount.toLocaleString()}
            </p>
            <p className="text-sm text-muted-foreground">records</p>
          </div>
          <DeltaIndicator
            delta={outcome.delta}
            neutralThreshold={DELTA_THRESHOLDS.neutral}
            isPercentage
            size="sm"
          />
        </div>

        {/* Top Causes */}
        <div className="flex-1">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Top Causes</p>
          <div className="space-y-2">
            {outcome.topCauses.slice(0, 5).map((cause) => (
              <button
                key={cause.labelId}
                onClick={() => onCauseClick(cause.labelId, cause.labelName)}
                className="w-full rounded-2xl px-3 py-2 text-left transition-colors hover:bg-accent/60 cursor-pointer"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="truncate text-sm font-medium">{cause.labelName}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {cause.percentage.toFixed(0)}%
                  </span>
                </div>
                <Progress
                  value={cause.percentage}
                  max={100}
                  className="h-1.5"
                />
              </button>
            ))}
            {outcome.topCauses.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No causes found</p>
            )}
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
